import React from 'react';

import { Body, Box } from '@edreamsodigeo/prisma-design-system';
import { ChronologyContent, NumberChronology } from '../Chronology';
import PropTypes from 'prop-types';
import {
  createFeature,
  Trans,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import { PriceStyle } from '../PriceStyle';
import {
  useFreeTrialPlusOffer,
  useFreeTrialPlusOfferSelector,
} from '@/providers/SubscriptionsProvider';
import {
  isYearlyPaidMonthlySubscription,
  selectSubscriptionPeriodInDays,
  selectSubscriptionPrice,
} from '@/selectors';
import { PRIME_FIRST_PHASE_2_MOBILE } from '@/versionedComponents';
import { PriceFreezeDateKey } from '@/components/ConfirmationStepSuccess/ConfirmationStepSuccess.utils';
import { usePrimeBenefitsLink } from '@/providers/PrimeBenefitsLinkProvider';

const DaysStyle = ({ days }) => (
  <Body inline type="xSmall">
    {days}
  </Body>
);

export function _PrimeChronologyStep({ stepIndex, lastStep = true }) {
  const { t } = useTranslation();
  const primeOffer = useFreeTrialPlusOffer();
  const hasInstallmentsPolicy = useFreeTrialPlusOfferSelector(
    isYearlyPaidMonthlySubscription
  );

  const descriptionKey = `pricefreeze.primeActivationStep.description.${
    hasInstallmentsPolicy ? 'monthlyInstallments' : 'regular'
  }`;

  const content = {
    heading: t('pricefreeze.primeActivationStep.heading', {
      freeTrialDays: selectSubscriptionPeriodInDays(primeOffer),
    }),
    title: t('pricefreeze.primeActivationStep.title'),
    description: (
      <Trans
        i18nKey={descriptionKey}
        components={{
          renewalPrice: <PriceStyle money={primeOffer.renewalPrice} />,
        }}
        values={{
          primeSavingAverage: t('variables.primeSavingAverage'),
        }}
      />
    ),
  };

  return (
    <NumberChronology number={stepIndex} last={lastStep}>
      <ChronologyContent heading={content.heading} title={content.title}>
        <Box pt={'2x_small'}>
          <Body type={'small'} color="neutral.90">
            {content.description}
          </Body>
        </Box>
      </ChronologyContent>
    </NumberChronology>
  );
}

export function _PrimeChronologyStepPhase2({ stepIndex, lastStep = true }) {
  const { t } = useTranslation();
  const primeBenefitsLink = usePrimeBenefitsLink();
  const hasInstallmentsPolicy = useFreeTrialPlusOfferSelector(
    isYearlyPaidMonthlySubscription
  );

  const subscription = {
    days: useFreeTrialPlusOfferSelector(selectSubscriptionPeriodInDays),
    price: useFreeTrialPlusOfferSelector(selectSubscriptionPrice),
  };

  const content = {
    heading: (
      <Trans
        i18nKey="pricefreeze.confirmationpage.step3.bullet"
        components={{
          dateKey: (
            <PriceFreezeDateKey
              expirationDate={getFreeTrialExpiration(subscription.days)}
            />
          ),
        }}
      />
    ),

    title: t('pricefreeze.confirmationpage.step3.title'),
  };

  return (
    <NumberChronology number={stepIndex} last={lastStep}>
      <ChronologyContent heading={content.heading} title={content.title}>
        <Box>
          <Box pt={'1x_small'}>
            <Body type={'small'} color="neutral.90">
              {t('pricefreeze.confirmationpage.step3.parag1', {
                numberPrimeMembers: t(
                  'variables.prime.numberOfMembersInMillions'
                ),
              })}
            </Body>
          </Box>
          <Box pt={'1x_small'}>
            <Body type={'small'} color="neutral.90">
              <Trans
                i18nKey="pricefreeze.confirmationpage.step3.primebenefitslink"
                components={{
                  benefitLink: primeBenefitsLink,
                }}
              />
            </Body>
          </Box>
          <Box pt={'1x_small'}>
            <Body type={'small'} color="neutral.90">
              <Trans
                i18nKey={`pricefreeze.confirmationpage.step3.parag2.${
                  hasInstallmentsPolicy ? 'yearly' : 'monthly'
                }`}
                components={{
                  daysKey: <DaysStyle days={subscription.days} />,
                  priceKey: <PriceStyle money={subscription.price} />,
                }}
              />
            </Body>
          </Box>
        </Box>
      </ChronologyContent>
    </NumberChronology>
  );
}

export const PrimeChronologyStep = createFeature({
  name: PRIME_FIRST_PHASE_2_MOBILE,
  variations: [
    _PrimeChronologyStep,
    _PrimeChronologyStepPhase2,
    _PrimeChronologyStepPhase2,
  ],
  version: 1,
});

PrimeChronologyStep.propTypes = {
  stepIndex: PropTypes.number.isRequired,
  lastStep: PropTypes.bool,
};

function getFreeTrialExpiration(days) {
  const freeTrialExpiration = new Date();
  freeTrialExpiration.setDate(freeTrialExpiration.getDate() + days);

  return freeTrialExpiration.getTime();
}
